// client/src/pages/RegisterPage.tsx
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { API_ENDPOINTS } from "../config/api";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff } from "lucide-react";
const RegisterPage = () => {
  const { t } = useTranslation();
  const [tenantName, setTenantName] = useState("");
  const [industry, setIndustry] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [userName, setUserName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const validateForm = () => {
    if (!tenantName.trim()) return t("registerPage.validation.tenantRequired");
    if (!userName.trim()) return t("registerPage.validation.userRequired");
    if (!/^[A-Za-z\s]+$/.test(userName.trim())) return t("registerPage.validation.userLettersOnly");

    if (!email.trim()) return t("registerPage.validation.emailRequired");
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) return t("registerPage.validation.invalidEmail");

    if (!password) return t("registerPage.validation.passwordRequired");
    if (password.length < 6) return t("registerPage.validation.passwordMinLength");
    if (!/[A-Z]/.test(password)) return t("registerPage.validation.passwordUppercase");
    if (!/[0-9]/.test(password)) return t("registerPage.validation.passwordNumber");

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    setIsLoading(true);
    const payload = {
      tenant: {
        name: tenantName,
        industry: industry || "General",
        subscription_start_date: new Date().toISOString().split("T")[0],
        subscription_end_date: null,
      },
      user: {
        name: userName,
        email,
      },
      password,
    };

    try {
      const res = await fetch(API_ENDPOINTS.REGISTER, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Registration failed");

      setSuccess(t("registerPage.successMessage"));
      setTimeout(() => navigate("/login"), 2000);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t("registerPage.validation.registrationFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen justify-center items-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="bg-white border border-gray-200 p-8 rounded-2xl shadow-xl w-full max-w-md">
        <h2 className="text-3xl font-bold mb-6 text-center text-gray-900">{t("registerPage.title")}</h2>
        <p className="text-center text-gray-500 mb-8">{t("registerPage.subtitle")}</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Tenant Name */}
          <div>
            <label htmlFor="tenantName" className="block text-sm font-medium text-gray-700 mb-1">
              {t("registerPage.tenantName")}
            </label>
            <input
              type="text"
              id="tenantName"
              placeholder={t("registerPage.tenantNamePlaceholder")}
              value={tenantName}
              disabled={isLoading}
              onChange={(e) => setTenantName(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white text-gray-900 placeholder-gray-400 transition-all"
              required
            />
          </div>

          {/* Industry */}
          <div>
            <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-1">
              {t("registerPage.industry")}
            </label>
            <input
              type="text"
              id="industry"
              placeholder={t("registerPage.industryPlaceholder")}
              value={industry}
              disabled={isLoading}
              onChange={(e) => setIndustry(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white text-gray-900 placeholder-gray-400 transition-all"
            />
          </div>

          {/* User Name */}
          <div>
            <label htmlFor="userName" className="block text-sm font-medium text-gray-700 mb-1">
              {t("registerPage.userName")}
            </label>
            <input
              type="text"
              id="userName"
              placeholder={t("registerPage.userNamePlaceholder")}
              value={userName}
              disabled={isLoading}
              onChange={(e) => setUserName(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white text-gray-900 placeholder-gray-400 transition-all"
              required
            />
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              {t("registerPage.email")}
            </label>
            <input
              type="email"
              id="email"
              placeholder={t("registerPage.emailPlaceholder")}
              value={email}
              disabled={isLoading}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white text-gray-900 placeholder-gray-400 transition-all"
              required
            />
          </div>

          {/* Password */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              {t("registerPage.password")}
            </label>

            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                placeholder={t("registerPage.passwordPlaceholder")}
                value={password}
                disabled={isLoading}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full p-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white text-gray-900 placeholder-gray-400 transition-all"
                required
              />

              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 outline-none focus:outline-none focus:ring-0 active:outline-none border-none"
                disabled={isLoading}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          {/* Error / Success Messages */}
          {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">{error}</div>}
          {success && <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-600 text-sm">{success}</div>}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex items-center justify-center bg-primary text-white py-3 rounded-lg font-medium hover:bg-blue-800 transition-colors shadow-sm mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <svg
                  className="animate-spin h-5 w-5 mr-2 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                {t("registerPage.registering")}
              </>
            ) : (
              t("registerPage.register")
            )}
          </button>
        </form>

        <p className="text-sm text-gray-600 text-center mt-6">
          {t("registerPage.alreadyHaveAccount")}{" "}
          <Link to="/login" className="text-primary font-semibold hover:text-blue-800 hover:underline">
            {t("registerPage.loginHere")}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
